import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Clock, FileText, Loader, ChevronDown, ChevronUp, X } from 'lucide-react';
import axios from 'axios';

// Modal Component
const Modal = ({ isOpen, onClose, title, message, type = 'info', onConfirm, confirmText, cancelText, showInput, inputPlaceholder, showFileUpload, showRentasId }) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [rentasId, setRentasId] = useState('');

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (onConfirm) {
            if (showFileUpload || showRentasId) {
                onConfirm({ file: selectedFile, rentasId });
            } else {
                onConfirm(showInput ? inputValue : true);
            }
        }
        setInputValue('');
        setSelectedFile(null);
        setRentasId('');
    };

    const handleClose = () => {
        setInputValue('');
        setSelectedFile(null);
        setRentasId('');
        onClose();
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const typeStyles = {
        success: {
            icon: CheckCircle,
            iconColor: 'text-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            buttonColor: 'bg-green-600 hover:bg-green-700'
        },
        error: {
            icon: XCircle,
            iconColor: 'text-red-600',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            buttonColor: 'bg-red-600 hover:bg-red-700'
        },
        warning: {
            icon: AlertTriangle,
            iconColor: 'text-primary-500',
            bgColor: 'bg-primary-50',
            borderColor: 'border-primary-200',
            buttonColor: 'bg-primary-500 hover:bg-primary-700'
        },
        info: {
            icon: FileText,
            iconColor: 'text-primary-500',
            bgColor: 'bg-primary-50',
            borderColor: 'border-primary-200',
            buttonColor: 'bg-primary-500 hover:bg-primary-700'
        }
    };

    const style = typeStyles[type] || typeStyles.info;
    const Icon = style.icon;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <X className="h-5 w-5" />
                </button>

                <div className="p-6">
                    <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${style.bgColor} ${style.borderColor} border-2 mb-4`}>
                        <Icon className={`h-6 w-6 ${style.iconColor}`} />
                    </div>

                    {title && (
                        <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                            {title}
                        </h3>
                    )}

                    <p className="text-sm text-gray-600 text-center mb-6">
                        {message}
                    </p>

                    {showInput && (
                        <div className="mb-6">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={inputPlaceholder || 'Enter text...'}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                            />
                        </div>
                    )}

                    {showFileUpload && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Reference (Optional)
                            </label>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {selectedFile && (
                                <p className="mt-1 text-xs text-gray-500">Selected: {selectedFile.name}</p>
                            )}
                        </div>
                    )}

                    {showRentasId && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                RENTAS reference (Optional)
                            </label>
                            <input
                                type="text"
                                value={rentasId}
                                onChange={(e) => setRentasId(e.target.value)}
                                placeholder="Enter RENTAS reference..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    )}

                    <div className="flex space-x-3">
                        {onConfirm && (
                            <button
                                onClick={handleConfirm}
                                className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-md ${style.buttonColor} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                            >
                                {confirmText || 'Confirm'}
                            </button>
                        )}
                        <button
                            onClick={handleClose}
                            className={`${onConfirm ? 'flex-1' : 'w-full'} px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
                        >
                            {cancelText || (onConfirm ? 'Cancel' : 'Close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const BondTokenRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRequest, setExpandedRequest] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [user, setUser] = useState(null);

    // Modal states
    const [modal, setModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        onConfirm: null,
        confirmText: '',
        cancelText: '',
        showInput: false,
        inputPlaceholder: '',
        showFileUpload: false,
        showRentasId: false
    });

    useEffect(() => {
        fetchUserInfo();
        fetchRequests();
    }, []);

    const fetchUserInfo = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/auth/profile', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.user) {
                setUser(response.data.user);
            }
        } catch (err) {
            console.error('Error fetching user info:', err);
        }
    };

    const fetchRequests = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await axios.get('/api/bond-token-requests/my', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setRequests(response.data.data);
            } else {
                setError('Failed to load bond token requests');
            }
        } catch (err) {
            console.error('Error fetching bond token requests:', err);
            setError(err.response?.data?.message || 'Failed to load bond token requests');
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setModal({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showFileUpload: false, showRentasId: false });
    };

    const showModal = (title, message, type = 'info', onConfirm = null, confirmText = '', cancelText = '', showInput = false, inputPlaceholder = '', showFileUpload = false, showRentasId = false) => {
        setModal({
            isOpen: true,
            title,
            message,
            type,
            onConfirm,
            confirmText,
            cancelText,
            showInput,
            inputPlaceholder,
            showFileUpload,
            showRentasId
        });
    };

    const handleApprove = async (requestId, level = 'L1') => {
        const confirmMessage = level === 'L1'
            ? `Are you sure you want to approve this bond token request at L1? This will move the request to L2 approval.`
            : `Are you sure you want to approve this bond token request at L2? This will transfer your cash tokens to the issuer and mint bond tokens to your wallet.`;

        showModal(
            `Approve ${level}`,
            confirmMessage,
            'warning',
            async (data) => {
                closeModal();
                try {
                    setActionLoading(requestId);

                    const token = localStorage.getItem('token');
                    const endpoint = level === 'L1' ? '/api/bond-token-requests/approve-l1' : '/api/bond-token-requests/approve-l2';

                    // Create FormData to handle file upload
                    const formData = new FormData();
                    formData.append('requestId', requestId);
                    formData.append('notes', '');

                    // Add optional fields if provided
                    if (data && data.file) {
                        formData.append('paymentReferenceFile', data.file);
                    }
                    if (data && data.rentasId) {
                        formData.append('rentasId', data.rentasId);
                    }

                    const response = await axios.post(endpoint, formData, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (response.data.success) {
                        const successMessage = level === 'L1'
                            ? 'Bond token request approved at L1. Awaiting L2 approval.'
                            : 'Bond token request approved at L2! Cash tokens transferred and bond tokens minted.';
                        showModal('Success', successMessage, 'success');
                        fetchRequests();
                    } else {
                        // Handle case where API returns success:false in response body
                        const errorMessage = response.data.message || response.data.error || 'Failed to approve request';
                        showModal('Error', errorMessage, 'error');
                    }
                } catch (err) {
                    console.error('Error approving request:', err);
                    // Extract the most specific error message available
                    const errorMessage = err.response?.data?.message ||
                        err.response?.data?.error ||
                        err.message ||
                        'Failed to approve request. Please try again.';
                    showModal('Error', errorMessage, 'error');
                } finally {
                    setActionLoading(null);
                }
            },
            'Approve',
            'Cancel',
            false,
            '',
            true,
            true
        );
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending L1 Approval' },
            pending_l2: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock, label: 'Pending L2 Approval' },
            approved: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircle, label: 'Approved' },
            rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' },
            completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Completed' },
            failed: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle, label: 'Failed' }
        };

        const config = statusConfig[status] || statusConfig.pending;
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                <Icon className="h-3 w-3 mr-1" />
                {config.label}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-MY', {
            style: 'currency',
            currency: 'MYR'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-MY', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading bond token requests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Modal
                isOpen={modal.isOpen}
                onClose={closeModal}
                title={modal.title}
                message={modal.message}
                type={modal.type}
                onConfirm={modal.onConfirm}
                confirmText={modal.confirmText}
                cancelText={modal.cancelText}
                showInput={modal.showInput}
                inputPlaceholder={modal.inputPlaceholder}
                showFileUpload={modal.showFileUpload}
                showRentasId={modal.showRentasId}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Receive Bond Token</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Review and receive bond tokens
                    </p>
                </div>                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex items-start">
                            <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="text-sm text-red-700 mt-1">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {requests.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Receive Bond Token Requests</h3>
                        <p className="text-gray-600">
                            You don't have any bond token requests at the moment. Requests will appear here when bonds with approved sub-agreements reach their issuance date.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {requests.map((request) => (
                            <div key={request._id} className="bg-white rounded-lg shadow overflow-hidden">
                                <div className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {request.bondSnapshot?.bondName || 'Unknown Bond'}
                                                </h3>
                                                <div className="ml-3">
                                                    {getStatusBadge(request.status)}
                                                </div>
                                            </div>
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-500">ISIN:</span>
                                                    <span className="ml-2 font-medium text-gray-900">
                                                        {request.bondSnapshot?.isin || '—'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Issuer:</span>
                                                    <span className="ml-2 font-medium text-gray-900">
                                                        {request.bondSnapshot?.issuer || '—'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Product Type:</span>
                                                    <span className="ml-2 font-medium text-gray-900">
                                                        {request.bondSnapshot?.productType === "Fixed Notes" ? "Conventional" : (request.bondSnapshot?.productType || '—')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-4 flex items-center space-x-6 text-sm">
                                                <div>
                                                    <span className="text-gray-500">Bond Token Amount:</span>
                                                    <span className="ml-2 font-semibold text-green-600">
                                                        {formatCurrency(request.bondTokenAmount)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Created:</span>
                                                    <span className="ml-2 text-gray-700">
                                                        {formatDate(request.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {request.status === 'pending' && (
                                            <div className="ml-4 flex flex-col space-y-2">
                                                <button
                                                    onClick={() => handleApprove(request._id, 'L1')}
                                                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    {actionLoading === request._id ? (
                                                        <>
                                                            <Loader className="h-4 w-4 mr-2 animate-spin" />
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CheckCircle className="h-4 w-4 mr-2" />
                                                            Approve L1
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {request.status === 'pending_l2' && (() => {
                                            // Check if current user approved L1
                                            if (!user || !request.l1ApprovedBy) return true;

                                            const l1ApproverId = typeof request.l1ApprovedBy === 'object'
                                                ? request.l1ApprovedBy._id
                                                : request.l1ApprovedBy;
                                            const l1ApproverEmail = typeof request.l1ApprovedBy === 'object'
                                                ? request.l1ApprovedBy.email
                                                : null;

                                            // Return false (hide) if current user approved L1
                                            return !(user._id === l1ApproverId || user.email === l1ApproverEmail);
                                        })() && (
                                                <div className="ml-4 flex flex-col space-y-2">
                                                    <div className="mb-2 p-2 bg-blue-50 rounded text-xs text-blue-800">
                                                        <strong>L1 Approved</strong>
                                                        {request.l1ApprovedAt && (
                                                            <div className="mt-1">
                                                                by {request.l1ApprovedBy?.email || 'Unknown'}
                                                                <br />
                                                                on {formatDate(request.l1ApprovedAt)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleApprove(request._id, 'L2')}
                                                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {actionLoading === request._id ? (
                                                            <>
                                                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                                Approve L2
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                    </div>

                                    {/* Expandable details section */}
                                    <button
                                        onClick={() => setExpandedRequest(expandedRequest === request._id ? null : request._id)}
                                        className="mt-4 flex items-center text-sm text-blue-600 hover:text-blue-800"
                                    >
                                        {expandedRequest === request._id ? (
                                            <>
                                                <ChevronUp className="h-4 w-4 mr-1" />
                                                Hide Details
                                            </>
                                        ) : (
                                            <>
                                                <ChevronDown className="h-4 w-4 mr-1" />
                                                Show Details
                                            </>
                                        )}
                                    </button>

                                    {expandedRequest === request._id && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                {/* L1 Approval Information */}
                                                {request.l1ApprovedBy && (
                                                    <>
                                                        <div>
                                                            <span className="text-gray-500">L1 Approved By:</span>
                                                            <p className="mt-1 text-gray-700">
                                                                {request.l1ApprovedBy?.email || 'Unknown'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">L1 Approved At:</span>
                                                            <p className="mt-1 text-gray-700">
                                                                {formatDate(request.l1ApprovedAt)}
                                                            </p>
                                                        </div>
                                                        {request.l1Notes && (
                                                            <div className="md:col-span-2">
                                                                <span className="text-gray-500">L1 Notes:</span>
                                                                <p className="mt-1 text-gray-700">
                                                                    {request.l1Notes}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* L2 Approval Information */}
                                                {request.l2ApprovedBy && (
                                                    <>
                                                        <div>
                                                            <span className="text-gray-500">L2 Approved By:</span>
                                                            <p className="mt-1 text-gray-700">
                                                                {request.l2ApprovedBy?.email || 'Unknown'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">L2 Approved At:</span>
                                                            <p className="mt-1 text-gray-700">
                                                                {formatDate(request.l2ApprovedAt)}
                                                            </p>
                                                        </div>
                                                        {request.l2Notes && (
                                                            <div className="md:col-span-2">
                                                                <span className="text-gray-500">L2 Notes:</span>
                                                                <p className="mt-1 text-gray-700">
                                                                    {request.l2Notes}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* Payment Reference File */}
                                                {request.paymentReferenceFile && (
                                                    <div className="md:col-span-2">
                                                        <span className="text-gray-500">Payment Reference:</span>
                                                        <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-2">
                                                                    <FileText className="h-4 w-4 text-gray-600" />
                                                                    <span className="text-sm text-gray-700">{request.paymentReferenceFile.originalName || request.paymentReferenceFile.filename}</span>
                                                                </div>
                                                                <div className="flex space-x-2">
                                                                    {request.paymentReferenceFile.path && (
                                                                        <>
                                                                            <a
                                                                                href={request.paymentReferenceFile.path}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                                                                            >
                                                                                Preview
                                                                            </a>
                                                                            <a
                                                                                href={request.paymentReferenceFile.path}
                                                                                download={request.paymentReferenceFile.originalName}
                                                                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                                                                            >
                                                                                Download
                                                                            </a>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {request.paymentReferenceFile.uploadedAt && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    Uploaded on {formatDate(request.paymentReferenceFile.uploadedAt)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* RENTAS reference */}
                                                {request.rentasId && (
                                                    <div>
                                                        <span className="text-gray-500">RENTAS reference:</span>
                                                        <p className="mt-1 text-gray-700">
                                                            {request.rentasId}
                                                        </p>
                                                    </div>
                                                )}

                                                {request.status === 'completed' && (
                                                    <>
                                                        <div>
                                                            <span className="text-gray-500">Cash Token Transfer Hash:</span>
                                                            <p className="mt-1 font-mono text-xs text-gray-700 break-all">
                                                                {request.cashTokenTransferHash || '—'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Bond Token Mint Hash:</span>
                                                            <p className="mt-1 font-mono text-xs text-gray-700 break-all">
                                                                {request.bondTokenMintHash || '—'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Completed At:</span>
                                                            <p className="mt-1 text-gray-700">
                                                                {formatDate(request.completedAt)}
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                                {request.status === 'approved' && request.approvedAt && (
                                                    <div>
                                                        <span className="text-gray-500">Approved At:</span>
                                                        <p className="mt-1 text-gray-700">
                                                            {formatDate(request.approvedAt)}
                                                        </p>
                                                    </div>
                                                )}
                                                {request.status === 'rejected' && (
                                                    <>
                                                        <div>
                                                            <span className="text-gray-500">Rejected At:</span>
                                                            <p className="mt-1 text-gray-700">
                                                                {formatDate(request.rejectedAt)}
                                                            </p>
                                                        </div>
                                                        {request.rejectionLevel && (
                                                            <div>
                                                                <span className="text-gray-500">Rejection Level:</span>
                                                                <p className="mt-1 text-gray-700">
                                                                    {request.rejectionLevel}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {request.rejectionReason && (
                                                            <div className="md:col-span-2">
                                                                <span className="text-gray-500">Rejection Reason:</span>
                                                                <p className="mt-1 text-gray-700">
                                                                    {request.rejectionReason}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                {request.status === 'failed' && request.errorMessage && (
                                                    <div className="md:col-span-2">
                                                        <span className="text-gray-500">Error Message:</span>
                                                        <p className="mt-1 text-red-600">
                                                            {request.errorMessage}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mt-4 p-3 bg-blue-50 rounded-md">
                                                <p className="text-sm text-blue-800">
                                                    <strong>Note:</strong> As a primary subscriber, bond tokens will be minted to your wallet after both L1 and L2 approvals are completed.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BondTokenRequests;
